import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { config } from '../../config';

// Define interface for extra options
interface ExtraOptions {
  authHeaders?: Record<string, string>;
}

// Custom base query that handles auth dynamically
const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Create a base query instance
  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    credentials: 'include',
  });

  const authHeaders = (extraOptions as ExtraOptions)?.authHeaders;

  if (typeof args === 'string') {
    args = { url: args };
  }

  const argsWithAuth = {
    ...args,
    headers: {
      ...args.headers,
      'X-Api-Key': config.API_KEY,
      ...(authHeaders || {}),
    },
  };

  return baseQuery(argsWithAuth, api, extraOptions);
};

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Project'],
  endpoints: (builder) => ({
    getProjects: builder.query({
      query: (params) => ({
        url: '/projects',
        method: 'GET',
        params,
      }),
      providesTags: ['Project'],
    }),
    getProjectById: builder.query({
      query: (id) => `/projects/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Project', id }],
    }),
    createProject: builder.mutation({
      query: (project) => ({
        url: '/projects',
        method: 'POST',
        body: project,
      }),
      invalidatesTags: ['Project'],
    }),
    updateProject: builder.mutation({
      query: ({ id, ...project }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        body: project,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Project', id }],
    }),
    deleteProject: builder.mutation({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project'],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} = projectsApi;
